import { useEffect, useMemo, useState } from "react";
import CalendarComponent from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { collection, getDocs, doc, getDoc } from "firebase/firestore";
import { db, auth } from "../../services/firebase";
import { useNavigate } from "react-router-dom";

const cardClass =
  "rounded-[24px] border border-gray-200 bg-white shadow-sm";

const firstNonEmpty = (...values) =>
  values.find((value) => value !== undefined && value !== null && String(value).trim() !== "") || "";

export default function AdminCalendar() {
  const navigate = useNavigate();

  const [value, setValue] = useState(new Date());
  const [allAssignments, setAllAssignments] = useState([]);
  const [userMap, setUserMap] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [driverFilter, setDriverFilter] = useState("all");

  useEffect(() => {
    const checkAccess = async () => {
      const user = auth.currentUser;
      if (!user) return navigate("/login");

      const snap = await getDoc(doc(db, "users", user.uid));
      if (!snap.exists() || snap.data().role !== "admin") {
        navigate("/not-authorized");
      }
    };
    checkAccess();
  }, [navigate]);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const [assignmentSnap, userSnap] = await Promise.all([
          getDocs(collection(db, "assignments")),
          getDocs(collection(db, "users")),
        ]);

        const assignments = assignmentSnap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));

        const users = {};
        userSnap.docs.forEach((u) => {
          const data = u.data();
          users[u.id] = data.email || data.displayName || u.id;
        });

        setAllAssignments(assignments);
        setUserMap(users);
      } catch (err) {
        console.error("Error loading calendar data:", err);
        setError("Failed to load assignments. Please try again later.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const getJobIdsForRun = (runRaw) => {
    if (!runRaw) return [];

    if (Array.isArray(runRaw)) {
      return runRaw
        .map((x) => (typeof x === "string" ? x : x?.id || x?.jobId))
        .filter(Boolean);
    }

    const jobsField =
      runRaw.jobs || runRaw.jobIds || runRaw.jobList || runRaw.jobIDs;

    if (Array.isArray(jobsField)) {
      return jobsField
        .map((x) => (typeof x === "string" ? x : x?.id || x?.jobId))
        .filter(Boolean);
    }

    if (jobsField && typeof jobsField === "object") {
      return Object.keys(jobsField);
    }

    if (typeof runRaw === "object") {
      const ignoreKeys = new Set([
        "name",
        "runName",
        "status",
        "notes",
        "driverId",
        "createdAt",
        "updatedAt",
      ]);

      return Object.keys(runRaw).filter((k) => !ignoreKeys.has(k));
    }

    return [];
  };

  const toMelbDateStr = (d) =>
    d.toLocaleDateString("en-CA", { timeZone: "Australia/Melbourne" });

  const dailySummary = useMemo(() => {
    const summary = {};

    for (const a of allAssignments) {
      const day = a.date;
      if (!day) continue;

      const runsObj = a.runs || {};
      const runMeta = a.runMeta || {};
      const runEntries = Object.entries(runsObj);
      const driverId = a.driverId || "Unknown";

      if (!summary[day]) {
        summary[day] = {
          totalRuns: 0,
          totalJobs: 0,
          drivers: new Set(),
          driverRunMap: {},
        };
      }

      const dayBucket = summary[day];
      dayBucket.drivers.add(driverId);

      if (!dayBucket.driverRunMap[driverId]) {
        dayBucket.driverRunMap[driverId] = { runsByKey: {} };
      }

      const driverBucket = dayBucket.driverRunMap[driverId];

      for (const [runKey, runValue] of runEntries) {
        const list = Array.isArray(runValue) ? runValue.filter(Boolean) : [];
        const firstJob = list.find((item) => item && !item.isWeighbridgeDocket) || list[0] || null;
        const meta = runMeta?.[runKey] || {};
        const runName = runValue?.runName || runValue?.name || runKey;
        const jobIds = getJobIdsForRun(runValue);
        const vehicleRego = firstNonEmpty(
          meta.vehicleRego,
          meta.rego,
          meta.truckRego,
          firstJob?.vehicleRego,
          firstJob?.rego,
          firstJob?.truckRego,
        );
        const vehicleLoadType = firstNonEmpty(
          meta.vehicleLoadType,
          meta.loadType,
          meta.unitType,
          meta.truckType,
          firstJob?.vehicleLoadType,
          firstJob?.loadType,
          firstJob?.unitType,
          firstJob?.truckType,
        );
        const vehicleLabel = firstNonEmpty(
          meta.vehicleLabel,
          meta.truckLabel,
          firstJob?.vehicleLabel,
          firstJob?.truckLabel,
          [vehicleRego, vehicleLoadType].filter(Boolean).join(" • "),
          "No rego recorded",
        );

        if (!driverBucket.runsByKey[runKey]) {
          driverBucket.runsByKey[runKey] = {
            runKey,
            name: runName,
            vehicleRego,
            vehicleLoadType,
            vehicleLabel,
            jobIds: new Set(),
          };
        } else {
          driverBucket.runsByKey[runKey].name =
            driverBucket.runsByKey[runKey].name || runName;
          driverBucket.runsByKey[runKey].vehicleRego =
            driverBucket.runsByKey[runKey].vehicleRego || vehicleRego;
          driverBucket.runsByKey[runKey].vehicleLoadType =
            driverBucket.runsByKey[runKey].vehicleLoadType || vehicleLoadType;
          driverBucket.runsByKey[runKey].vehicleLabel =
            driverBucket.runsByKey[runKey].vehicleLabel || vehicleLabel;
        }

        const agg = driverBucket.runsByKey[runKey];
        const before = agg.jobIds.size;
        jobIds.forEach((id) => agg.jobIds.add(id));
        const after = agg.jobIds.size;

        if (before === 0) dayBucket.totalRuns += 1;
        if (after > before) dayBucket.totalJobs += after - before;
      }
    }

    return summary;
  }, [allAssignments]);

  const selectedDateStr = toMelbDateStr(value);
  const selectedSummary = dailySummary[selectedDateStr];
  const todayStr = toMelbDateStr(new Date());

  useEffect(() => {
    setDriverFilter("all");
  }, [selectedDateStr]);

  const openHistoryForDateAndDriver = (driverId, runKey = "") => {
    const p = new URLSearchParams({
      tab: "view-history",
      date: selectedDateStr,
      driverId,
    });
    if (runKey) p.set("runKey", runKey);

    navigate(`/dashboard?${p.toString()}`);
  };

  const jumpToToday = () => setValue(new Date());

  const truckCardsForSelectedDay = useMemo(() => {
    if (!selectedSummary) return [];

    const driverIds = Array.from(selectedSummary.drivers);
    const filtered = driverIds.filter((driverId) =>
      driverFilter === "all" ? true : driverId === driverFilter,
    );

    return filtered.flatMap((driverId) => {
      const driverLabel = userMap[driverId] || driverId;
      const runsByKey =
        selectedSummary.driverRunMap?.[driverId]?.runsByKey || {};

      const runsByTruck = {};
      Object.values(runsByKey).forEach((r) => {
        const vehicleLabel = r.vehicleLabel || r.vehicleRego || "No rego recorded";
        const key = r.vehicleRego || vehicleLabel || "no-rego";
        if (!runsByTruck[key]) {
          runsByTruck[key] = {
            cardKey: `${driverId}-${key}`,
            driverId,
            driverLabel,
            vehicleLabel,
            vehicleRego: r.vehicleRego || "",
            runs: [],
          };
        }
        runsByTruck[key].runs.push({
          runKey: r.runKey,
          name: r.name || r.runKey,
          vehicleRego: r.vehicleRego || "",
          vehicleLabel,
          jobCount: r.jobIds?.size || 0,
        });
      });

      return Object.values(runsByTruck).map((card) => {
        const runs = card.runs.sort((a, b) => a.name.localeCompare(b.name));
        const totalJobsForTruck = runs.reduce((sum, r) => sum + r.jobCount, 0);

        return {
          ...card,
          runs,
          totalJobsForTruck,
        };
      });
    });
  }, [selectedSummary, driverFilter, userMap]);

  const totalDaysWithRuns = useMemo(
    () =>
      Object.keys(dailySummary).filter((day) => dailySummary[day]?.totalRuns > 0)
        .length,
    [dailySummary],
  );

  const tileContent = ({ date, view }) => {
    if (view !== "month") return null;

    const dateStr = toMelbDateStr(date);
    const info = dailySummary[dateStr];
    const isToday = dateStr === todayStr;

    return (
      <div className="mt-2 flex flex-col items-center gap-1">
        {info ? (
          <>
            <div className="text-[11px] font-semibold text-emerald-700">
              {info.totalRuns} run{info.totalRuns > 1 ? "s" : ""}
            </div>
            <div className="text-[10px] text-gray-500">
              {info.totalJobs} job{info.totalJobs !== 1 ? "s" : ""}
            </div>
          </>
        ) : (
          <div className="h-[28px]" />
        )}

        {isToday && (
          <div className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-600">
            Today
          </div>
        )}
      </div>
    );
  };

  const tileClassName = ({ date, view }) => {
    if (view !== "month") return "";

    const dateStr = toMelbDateStr(date);
    const info = dailySummary[dateStr];
    const isToday = dateStr === todayStr;
    const isSelected = dateStr === selectedDateStr;

    let classes =
      "!rounded-2xl !border !border-gray-100 !bg-white text-gray-900 transition duration-150 hover:!bg-gray-50";

    if (info?.totalRuns) {
      if (info.totalRuns <= 3) {
        classes += " !bg-emerald-50/70";
      } else if (info.totalRuns <= 6) {
        classes += " !bg-emerald-100/80";
      } else {
        classes += " !bg-emerald-200/80";
      }
    }

    if (isToday) {
      classes += " !border-emerald-300";
    }

    if (isSelected) {
      classes += " ring-2 ring-emerald-600 ring-inset";
    }

    return classes;
  };

  return (
    <div className="w-full px-4 py-4 sm:px-6 sm:py-6 xl:px-8 space-y-6 text-gray-900">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
        <div className="text-xs text-gray-600">
          <span className="font-semibold text-gray-900">{selectedDateStr}</span>
          {" · "}
          <span className="font-semibold text-gray-900">{selectedSummary?.totalRuns || 0}</span> runs
          {" · "}
          <span className="font-semibold text-gray-900">{selectedSummary?.totalJobs || 0}</span> jobs
          {" · "}
          {totalDaysWithRuns} active days
        </div>
        <button
          type="button"
          onClick={jumpToToday}
          className="inline-flex items-center rounded-xl border border-gray-200 bg-white px-3.5 py-2 text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-50"
        >
          Jump to today ({todayStr})
        </button>
      </div>

      {error && (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1.65fr_0.9fr]">
        <div className={cardClass}>
          <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4 sm:px-6">
            <div>
              <h3 className="text-base font-semibold text-gray-900">
                Calendar overview
              </h3>
              <p className="mt-0.5 text-xs text-gray-500">
                Cleaner month view with clearer day states and spacing.
              </p>
            </div>
            {isLoading && (
              <span className="text-xs text-gray-500 animate-pulse">
                Loading assignments...
              </span>
            )}
          </div>

          <div className="p-4 sm:p-5">
            <div className="overflow-hidden rounded-[22px] border border-gray-200 bg-gray-50 p-3 sm:p-4">
              <CalendarComponent
                value={value}
                onChange={setValue}
                tileContent={tileContent}
                tileClassName={tileClassName}
                showNeighboringMonth={false}
                showDoubleView={false}
                showFixedNumberOfWeeks={false}
                prevLabel="‹"
                nextLabel="›"
                prev2Label="«"
                next2Label="»"
                formatShortWeekday={(locale, date) =>
                  date
                    .toLocaleDateString("en-US", { weekday: "short" })
                    .toUpperCase()
                }
                className={[
                  "w-full border-0 bg-transparent",
                  "[&_.react-calendar__navigation]:mb-4",
                  "[&_.react-calendar__navigation]:flex",
                  "[&_.react-calendar__navigation]:items-center",
                  "[&_.react-calendar__navigation]:justify-between",
                  "[&_.react-calendar__navigation__label]:grow-0",
                  "[&_.react-calendar__navigation__label]:rounded-xl",
                  "[&_.react-calendar__navigation__label]:px-4",
                  "[&_.react-calendar__navigation__label]:py-2",
                  "[&_.react-calendar__navigation__label]:text-lg",
                  "[&_.react-calendar__navigation__label]:font-semibold",
                  "[&_.react-calendar__navigation__label]:text-gray-900",
                  "[&_.react-calendar__navigation__arrow]:rounded-xl",
                  "[&_.react-calendar__navigation__arrow]:px-3",
                  "[&_.react-calendar__navigation__arrow]:py-2",
                  "[&_.react-calendar__navigation__arrow]:text-lg",
                  "[&_.react-calendar__navigation__arrow]:font-semibold",
                  "[&_.react-calendar__navigation__arrow]:text-gray-700",
                  "[&_.react-calendar__navigation__arrow]:transition",
                  "[&_.react-calendar__navigation__arrow:hover]:bg-white",
                  "[&_.react-calendar__month-view__weekdays]:mb-2",
                  "[&_.react-calendar__month-view__weekdays]:text-center",
                  "[&_.react-calendar__month-view__weekdays]:text-[11px]",
                  "[&_.react-calendar__month-view__weekdays]:font-semibold",
                  "[&_.react-calendar__month-view__weekdays]:uppercase",
                  "[&_.react-calendar__month-view__weekdays]:tracking-[0.14em]",
                  "[&_.react-calendar__month-view__weekdays]:text-gray-500",
                  "[&_.react-calendar__month-view__weekdays_abbr]:no-underline",
                  "[&_abbr]:no-underline",
                  "[&_.react-calendar__month-view__days]:gap-y-2",
                  "[&_.react-calendar__tile]:min-h-[110px]",
                  "[&_.react-calendar__tile]:p-3",
                  "[&_.react-calendar__tile]:text-center",
                  "[&_.react-calendar__tile]:align-top",
                  "[&_.react-calendar__tile]:font-medium",
                  "[&_.react-calendar__tile]:transition",
                  "[&_.react-calendar__tile--now]:bg-transparent",
                  "[&_.react-calendar__tile--active]:bg-transparent",
                ].join(" ")}
                style={{ width: "100%" }}
              />
            </div>

            <div className="mt-4 flex flex-wrap gap-3 text-[11px] text-gray-600">
              <div className="flex items-center gap-2">
                <span className="h-3 w-3 rounded border border-gray-200 bg-white" />
                No runs
              </div>
              <div className="flex items-center gap-2">
                <span className="h-3 w-3 rounded border border-emerald-100 bg-emerald-50" />
                1–3 runs
              </div>
              <div className="flex items-center gap-2">
                <span className="h-3 w-3 rounded border border-emerald-200 bg-emerald-100" />
                4–6 runs
              </div>
              <div className="flex items-center gap-2">
                <span className="h-3 w-3 rounded border border-emerald-300 bg-emerald-200" />
                7+ runs
              </div>
              <div className="flex items-center gap-2">
                <span className="h-3 w-3 rounded border border-emerald-300 bg-white" />
                Today
              </div>
              <div className="flex items-center gap-2">
                <span className="h-3 w-3 rounded border border-emerald-600 bg-white" />
                Selected day
              </div>
            </div>
          </div>
        </div>

        <div className={cardClass}>
          <div className="flex items-start justify-between gap-3 border-b border-gray-200 px-5 py-4 sm:px-6">
            <div>
              <h4 className="text-base font-semibold text-gray-900">
                Details for {selectedDateStr}
              </h4>
              {selectedSummary ? (
                <p className="mt-0.5 text-xs text-gray-500">
                  {selectedSummary.totalRuns} run
                  {selectedSummary.totalRuns > 1 ? "s" : ""} •{" "}
                  {selectedSummary.totalJobs} job
                  {selectedSummary.totalJobs !== 1 ? "s" : ""} •{" "}
                  {selectedSummary.drivers.size} driver
                  {selectedSummary.drivers.size > 1 ? "s" : ""}
                </p>
              ) : (
                <p className="mt-0.5 text-xs text-gray-500">
                  No assignments for this day.
                </p>
              )}
            </div>

            {selectedSummary && (
              <select
                value={driverFilter}
                onChange={(e) => setDriverFilter(e.target.value)}
                className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                <option value="all">All drivers</option>
                {Array.from(selectedSummary.drivers).map((driverId) => (
                  <option key={driverId} value={driverId}>
                    {userMap[driverId] || driverId}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="p-4 sm:p-5">
            {isLoading ? (
              <div className="space-y-3">
                <div className="h-3 w-32 rounded bg-gray-100 animate-pulse" />
                <div className="h-3 w-40 rounded bg-gray-100 animate-pulse" />
                <div className="h-24 w-full rounded-2xl bg-gray-100 animate-pulse" />
              </div>
            ) : !selectedSummary ? (
              <div className="flex min-h-[320px] items-center justify-center text-center">
                <p className="text-sm text-gray-500">
                  No runs assigned for this date.
                </p>
              </div>
            ) : (
              <div className="space-y-3 overflow-y-auto pr-1 max-h-[560px]">
                {truckCardsForSelectedDay.map((d) => (
                  <div
                    key={d.cardKey}
                    role="button"
                    tabIndex={0}
                    onClick={() => openHistoryForDateAndDriver(d.driverId)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        openHistoryForDateAndDriver(d.driverId);
                      }
                    }}
                    className="group cursor-pointer rounded-2xl border border-gray-200 bg-gray-50/80 p-4 shadow-sm transition hover:border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-emerald-600"
                    title="Click to view history for this truck and date"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="text-sm font-semibold text-gray-900">
                          {d.vehicleLabel}
                        </div>
                        <div className="text-xs text-gray-500">
                          {d.runs.length} run{d.runs.length > 1 ? "s" : ""} •{" "}
                          {d.totalJobsForTruck} job
                          {d.totalJobsForTruck !== 1 ? "s" : ""}
                        </div>
                        <div className="text-[11px] text-gray-400">
                          Driver: {d.driverLabel}
                        </div>
                      </div>
                      <div className="text-[11px] font-medium text-emerald-700 opacity-0 transition group-hover:opacity-100">
                        View history →
                      </div>
                    </div>

                    <div className="mt-3 space-y-2">
                      {d.runs.map((run) => (
                        <div
                          key={`${d.driverId}-${run.runKey}`}
                          role="button"
                          tabIndex={0}
                          onClick={(event) => {
                            event.stopPropagation();
                            openHistoryForDateAndDriver(d.driverId, run.runKey);
                          }}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              event.stopPropagation();
                              openHistoryForDateAndDriver(d.driverId, run.runKey);
                            }
                          }}
                          className="flex items-center justify-between rounded-xl border border-gray-100 bg-white px-3 py-2 text-xs"
                          title="View history for this run"
                        >
                          <div>
                            <div className="font-medium text-gray-800">
                              {run.name}
                            </div>
                            <div className="text-[11px] text-gray-500">
                              Run key: {run.runKey}
                            </div>
                          </div>
                          <div className="text-[11px] text-gray-500">
                            {run.jobCount} job
                            {run.jobCount !== 1 ? "s" : ""}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {!isLoading && (
              <div className="mt-4 border-t border-gray-100 pt-4 text-xs text-gray-500">
                This view is based on assignment documents for the selected day.
                Click a truck card to open View History with that date and truck
                pre-selected.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
